import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../ampli';
import { Icon } from '../../../components/common/icon/Icon';
import { SpinnerLoader } from '../../../components/common/loading/SpinnerLoader';
import { PopupConfirmModal } from '../../../components/common/modal/popup/PopupConfirmModal';
import { usePopup } from '../../../hooks/popup/usePopup';
import { useLayout } from '../../../hooks/screen/useLayout';
import { useAppDispatch, useAppSelector } from '../../../hooks/store/useStore';
import { userSellTractorSurveySlice } from '../../../store/userSellTractorSurveySlice';
import { pageInfo } from '../../../constants/pageInfo/pageInfo.constant';

interface SellTractorSurveyPageWrapperProps {
  headerTitle: string;
  description?: string;
  children: JSX.Element;
  isLoading?: boolean;
  canConfirm: boolean;
  confirmHandler?: () => void;
  closeHandler?: () => void;
  confirmButtonText?: string;
  prevButtonText?: string;
  prevButtonHandler?: () => void;
  buttonTooltip?: JSX.Element;
  steps?: string[];
  currentStep?: number;
}

export const SellTractorSurveyPageWrapper = ({
  headerTitle,
  description,
  children,
  isLoading,
  canConfirm,
  confirmHandler,
  closeHandler,
  confirmButtonText = '다음',
  prevButtonText = '이전',
  prevButtonHandler,
  buttonTooltip,
  steps,
  currentStep,
}: SellTractorSurveyPageWrapperProps) => {
  const location = useLocation();
  const Popup = usePopup();
  const navigate = useNavigate();

  const [isCloseButtonClicked, setIsCloseButtonClicked] = useState<boolean>(false);

  const userSellTractorSurveySelector = useAppSelector((state) => state.userSellTractorSurvey);
  const dispatch = useAppDispatch();
  const userSellTractorSurveyActions = userSellTractorSurveySlice.actions;

  const handleSurveyCancelButtonClick = () => {
    if (closeHandler) {
      closeHandler();
      return;
    }
    setIsCloseButtonClicked(true);
    ampli.서베이취소버튼클릭({ '서베이 이름': '회원 판매 트랙터' });
  };

  useLayout({
    fixedHeader: (
      <>
        <CloseButtonSection>
          <CloseButton onClick={handleSurveyCancelButtonClick}>
            <Icon name="CloseGrey" width={20} height={20} />
          </CloseButton>
        </CloseButtonSection>
        {steps && currentStep ? (
          <StepBarContainer>
            {steps.map((step, index) => {
              return <StepBarItem key={step} isCompleted={currentStep >= index + 1} />;
            })}
          </StepBarContainer>
        ) : null}
      </>
    ),
    fixedFooter: (
      <ButtonSection>
        <BlurredSection />
        {buttonTooltip && buttonTooltip}
        {prevButtonHandler && (
          <PrevButton
            type={'button'}
            disabled={isLoading}
            onClick={() => {
              if (prevButtonHandler) {
                prevButtonHandler();
              }
            }}
          >
            <PrevButtonText>{prevButtonText}</PrevButtonText>
          </PrevButton>
        )}

        <ConfirmButton
          id={confirmButtonText === '완료' ? 'sell-tractor-complete' : ''}
          type={'button'}
          disabled={!canConfirm || isLoading}
          onClick={() => {
            if (confirmHandler) {
              confirmHandler();
            }
          }}
        >
          <SpinnerLoader isVisible={isLoading ?? false} />
          {!isLoading && <ConfirmButtonText>{confirmButtonText}</ConfirmButtonText>}
        </ConfirmButton>
      </ButtonSection>
    ),
    fixedHeaderHeight: 74,
    fixedFooterHeight: 72,
  });

  useEffect(() => {
    if (
      location.pathname !== '/survey/progress/sell-tractor/required-input-overview' &&
      location.pathname !== '/survey/progress/sell-tractor/own-tractor-list'
    ) {
      if (!userSellTractorSurveySelector.isSellTractorSurveyStartedProperly) {
        Popup.toast('비정상적인 접근입니다.');
        navigate('/survey/entry/sell-tractor');
      }
    }
  }, [location.pathname, userSellTractorSurveySelector.isSellTractorSurveyStartedProperly, Popup, navigate]);

  return (
    <>
      <Container>
        <TopSection>
          <HeaderSection>
            <HeaderContainer>
              <Header>{headerTitle}</Header>
              {description && <Description>{description}</Description>}
            </HeaderContainer>
          </HeaderSection>

          {children}
        </TopSection>
      </Container>

      <PopupConfirmModal
        isShow={isCloseButtonClicked}
        hasCloseButton={false}
        closeModal={() => setIsCloseButtonClicked(false)}
        description={'작성한 내용을 잃게 돼요!\n그래도 중단하고 나가시겠어요?'}
        buttonArray={[
          {
            text: userSellTractorSurveySelector.sellTractorIdForEdit ? '처음으로' : '홈으로',
            onClick: () => {
              const redirectUrl = userSellTractorSurveySelector.sellTractorIdForEdit
                ? pageInfo.myInfoSellTractorListPage.path
                : '/home';
              navigate(redirectUrl, { replace: true });
              dispatch(userSellTractorSurveyActions.changeIsSellTractorSurveyStartedProperly(false));
            },
            color: 'blue',
          },
          {
            text: '닫기',
            onClick: () => setIsCloseButtonClicked(false),
            color: 'gray',
          },
        ]}
      />
    </>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  width: 100%;
  padding-bottom: 40px;
`;

const TopSection = styled.div`
  display: flex;
  overflow: hidden;
  width: 100%;
  height: 100%;

  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
`;

const CloseButtonSection = styled.div`
  width: 100%;
  height: 40px;

  display: flex;
  justify-content: flex-end;
  align-items: center;

  padding: 0px var(--radius-large, 16px);
`;

const CloseButton = styled.button``;

const HeaderSection = styled.div`
  display: flex;
  padding: 24px 0px;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--radius-large, 16px);
`;

const Header = styled.pre`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/XX-Large/xxl-bold */
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 31.2px */

  word-break: break-all;
`;

const Description = styled.pre`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  text-align: center;

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */
`;

const ButtonSection = styled.div`
  position: relative;
  display: flex;
  width: 100%;

  padding: 0px 16px 32px 16px;
  background-color: white;

  justify-content: center;
  align-items: center;

  gap: 8px;
`;

const BlurredSection = styled.div`
  width: 100%;
  height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: var(--radius-large, 16px);
  gap: var(--radius-small, 4px);

  border-radius: var(--radius-none, 0px);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 63.96%);

  position: absolute;
  top: -40px;
`;

const Button = styled.button`
  width: 100%;
  height: 40px;
  max-height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 16px;

  gap: 4px;

  border-radius: var(--radius-medium, 8px);

  &:disabled {
    opacity: 0.3;
  }

  cursor: pointer;
`;

const ButtonText = styled.span`
  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;

const ConfirmButton = styled(Button)`
  background: var(--Light-Scheme-Color-primary-blue, #445eff);
`;

const ConfirmButtonText = styled(ButtonText)`
  color: var(--Light-Scheme-Monochrome-white, #fff);
`;

const PrevButton = styled(Button)`
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const PrevButtonText = styled(ButtonText)`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);
`;

const StepBarContainer = styled.div`
  display: flex;
  padding: var(--radius-large, 16px);
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`;

const StepBarItem = styled.div<{ isCompleted?: boolean }>`
  display: flex;
  height: 2px;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;
  flex: 1 0 0;

  border-radius: var(--radius-small, 4px);
  background: ${(props) => (props.isCompleted ? '#505363' : '#e4e4ed')};
`;
